import { hexToRGB, pxToDP, type TranslationIDs, useChipList, useIntl } from '@capsicum/common';
import { type FC, type SVGProps, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { useRTL } from '../../../hooks/helpers/useRTL';
import { useScrollInfo } from '../../../hooks/helpers/useScrollInfo';
import { CarouselArrow } from '../../atoms';
import { Chip } from '../../atoms/chip/Chip';

export type ChipListItem = {
	description?: string;
	i18nKey?: TranslationIDs;
	iconEnd?: FC<SVGProps<SVGSVGElement>> | undefined;
	iconStart?: FC<SVGProps<SVGSVGElement>> | undefined;
	name: string;
	selected?: boolean;
	status?: 'disabled' | 'enabled' | string;
	testID?: string;
	value: string;
};

export type ChipListProps = {
	allButton?: boolean;
	allChipAtEnd?: boolean;
	arrowsBackgroundColor?: string;
	attachToScreenValue?: `${number}px`;
	defaultSelection?: string | undefined;
	hasWrapper?: boolean;
	items: Array<ChipListItem>;
	itemsSelected: (selected: Array<string>) => void;
	onItemSelectedCustom?: (
		item: string,
		selectedItems: Array<string>,
		setSelectedItems: (items: Array<string>) => void
	) => void;
	selectionType?: 'multiple' | 'single';
	showScrollingArrows?: boolean;
	testID?: string;
};

/**
 * ChipList is a List used to contain an array of <Chip> components.
 *
 * @param {boolean} props.allButton Show the All chip option
 * @param {boolean} props.allChipAtEnd Places the All chip at the end of the list
 * @param {string} props.arrowsBackgroundColor Background color for scrolling left/right arrows.
 * @param {`${number}px`} props.attachToScreenValue Approaches the list to the screen on both sides by the specific px value when scrolling. hasWrapper property needs to be false
 * @param {string} props.defaultSelection Default selected item
 * @param {boolean} props.hasWrapper Adds a default wrapper with margin to the list
 * @param {Array<ChipItems>} props.items The List of Items to use as filters
 * @param {(selected: Array<string>) => void} props.itemsSelected Notify page what are the selected Elements
 * @param {(item: string, selectedItems: Array<string>, setSelectedItems: (items: Array<string>) => void) => void} props.onItemSelectedCustom Function will be called on item selection to let you define which items will be selected as result after the action
 * @param {'multiple' | 'single'} props.selectionType Type of selection allowed
 * @param {boolean} props.showScrollingArrows Indicates whether to show scrolling left/right arrows.
 * @param {string} props.testID Test identifier for testing
 * @returns {JSX.Element} The Chip Component
 */

export const ChipList = ({
	allButton = true,
	allChipAtEnd = false,
	arrowsBackgroundColor: arrowsBackgroundColorProp,
	attachToScreenValue = '0px',
	defaultSelection = undefined,
	hasWrapper = true,
	items,
	itemsSelected,
	onItemSelectedCustom,
	selectionType = 'multiple',
	showScrollingArrows = false,
	testID
}: ChipListProps): JSX.Element => {
	const {
		colors: {
			backgrounds: {
				main: { screen }
			}
		}
	} = useTheme();

	const { isSelected, onChipClick } = useChipList(
		itemsSelected,
		items,
		selectionType,
		defaultSelection,
		onItemSelectedCustom
	);

	const { $t } = useIntl();
	const { isRTL } = useRTL();
	const { elementRef, onScrollLeft, onScrollRight, scrollLeft, scrollWidth, size, width } = useScrollInfo();

	const areArrowsEnabled = useMemo(() => showScrollingArrows && size !== 'mobile', [showScrollingArrows, size]);

	const isLeftArrowVisible = useMemo(
		() => areArrowsEnabled && (isRTL ? scrollWidth + scrollLeft > width : scrollLeft > 0),
		[scrollLeft, scrollWidth, width, areArrowsEnabled, isRTL]
	);
	const isRightArrowVisible = useMemo(() => {
		// Account for rounding errors of scrollWidth (integer) vs width (float) by checking if the difference is less than 1
		return (
			areArrowsEnabled &&
			(isRTL
				? scrollLeft < 0
				: scrollLeft + width < scrollWidth && Math.abs(scrollLeft + width - scrollWidth) >= 1)
		);
	}, [scrollLeft, scrollWidth, width, areArrowsEnabled, isRTL]);

	const isAttachToScreenValueSet = attachToScreenValue !== '0px';
	const arrowsBackgroundColor = arrowsBackgroundColorProp ?? screen;
	const isAllButtonVisible = allButton && selectionType === 'multiple';

	return (
		<S.Wrapper
			$attachToScreenValue={attachToScreenValue}
			$hasWrapper={hasWrapper}
			data-testid={testID}
			ref={elementRef}
		>
			{isAllButtonVisible && (
				<Chip
					isCustomSelectionMode={!!onItemSelectedCustom}
					isSelected={isSelected('all')}
					style={!hasWrapper && isAttachToScreenValueSet ? { marginLeft: pxToDP(attachToScreenValue) } : {}}
					testID="chip-product-all"
					text={$t('global.all')}
					value="all"
					onClick={onChipClick}
				/>
			)}
			{items?.map((item, index) => {
				const { status = 'enabled' } = item;

				if (item.value === 'unselected') {
					return null;
				}
				const hasMarginLeft =
					index === 0 && !hasWrapper && !isAllButtonVisible && isAttachToScreenValueSet
						? { marginLeft: pxToDP(attachToScreenValue) }
						: {};
				const chipName = item.i18nKey ? $t(item.i18nKey) : item.name;

				return (
					<Chip
						disabled={status === 'disabled'}
						iconEnd={item.iconEnd}
						iconStart={item.iconStart}
						isCustomSelectionMode={!!onItemSelectedCustom}
						isSelected={isSelected(item.value)}
						key={item.name}
						selectionType={selectionType}
						style={hasMarginLeft}
						testID={item.testID ? item.testID : `chip-${item.description}`}
						text={chipName}
						value={item.value}
						onClick={onChipClick}
					/>
				);
			})}

			{allChipAtEnd && (
				<Chip
					isCustomSelectionMode={!!onItemSelectedCustom}
					isSelected={isSelected('all')}
					style={!hasWrapper && isAttachToScreenValueSet ? { marginLeft: pxToDP(attachToScreenValue) } : {}}
					testID="chip-product-all"
					text={$t('global.all')}
					value="all"
					onClick={onChipClick}
				/>
			)}

			{isLeftArrowVisible && (
				<S.ArrowWrapper
					$attachToScreenValue={attachToScreenValue}
					$backgroundColor={arrowsBackgroundColor}
					$direction="left"
					$isAttachToScreenValueSet={isAttachToScreenValueSet}
					$isRTL={isRTL}
					data-testid="chiplist-previous-arrow"
					role="button"
					onClick={onScrollLeft}
				>
					<CarouselArrow direction="left" />
				</S.ArrowWrapper>
			)}

			{isRightArrowVisible && (
				<S.ArrowWrapper
					$attachToScreenValue={attachToScreenValue}
					$backgroundColor={arrowsBackgroundColor}
					$direction="right"
					$isAttachToScreenValueSet={isAttachToScreenValueSet}
					$isRTL={isRTL}
					data-testid="chiplist-next-arrow"
					role="button"
					onClick={onScrollRight}
				>
					<CarouselArrow direction="right" />
				</S.ArrowWrapper>
			)}
		</S.Wrapper>
	);
};
namespace S {
	export const Wrapper = styled.div<{
		$attachToScreenValue: `${number}px`;
		$hasWrapper: boolean;
	}>`
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		padding: ${({ $hasWrapper, theme }) => ($hasWrapper ? theme.spacings.large : `0`)};
		margin: ${({ $attachToScreenValue, $hasWrapper }) => ($hasWrapper ? `0` : `0 -${$attachToScreenValue}`)};
		overflow-x: auto;
		/* for Firefox */
		scrollbar-width: none;
		/* for Chrome, Safari, and Opera */
		&::-webkit-scrollbar {
			display: none;
		}
	`;

	export const ArrowWrapper = styled.div<{
		$attachToScreenValue: string;
		$backgroundColor: string;
		$direction: 'left' | 'right';
		$isAttachToScreenValueSet: boolean;
		$isRTL: boolean;
	}>`
		z-index: 20;
		top: 0;
		bottom: 0;
		display: flex;
		cursor: pointer;
		user-select: none;
		position: absolute;
		align-items: center;

		${({ theme }) => theme.border.radius.circle};
		width: ${({ theme }) => theme.sizing.xxlarge};

		/**For covering up whole chipList height */
		height: 101%;

		${({ $attachToScreenValue, $backgroundColor, $direction, $isAttachToScreenValueSet, $isRTL }) =>
			$direction === 'left'
				? css`
						border-top-left-radius: 0;
						justify-content: ${$isRTL ? 'flex-end' : 'flex-start'};
						border-bottom-left-radius: 0;
						left: ${!$isAttachToScreenValueSet
							? '-1px' // For covering whole chipList area and leaving no blank zones.
							: `-${$attachToScreenValue}`};
						background: linear-gradient(
							to left,
							${hexToRGB($backgroundColor, 0)} 0%,
							${hexToRGB($backgroundColor, 1)} 50%
						);
					`
				: css`
						justify-content: ${$isRTL ? 'flex-start' : 'flex-end'};
						border-top-right-radius: 0;
						border-bottom-right-radius: 0;
						right: ${!$isAttachToScreenValueSet
							? '-1px' // For covering whole chipList area and leaving no blank zones.
							: `-${$attachToScreenValue}`};

						background: linear-gradient(
							to right,
							${hexToRGB($backgroundColor, 0)} 0%,
							${hexToRGB($backgroundColor, 1)} 50%
						);
					`};
	`;
}
